// =============================================================================
// All Stories - Admin view of all users' stories with filters
// =============================================================================

import React, { useEffect, useState } from 'react';
import {
  Loader2,
  FileText,
  Calendar,
  User as UserIcon,
  Filter,
  X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  StoryGroup,
  getStoryGroups,
  getStoryGroup,
  getUsersWithStories,
  UserWithStories,
} from '../services/simpleStore';
import { getUserProfile } from '../services/store';
import StoryGroupCard from '../components/StoryGroupCard';
import LanguageSelector from '../components/LanguageSelector';
import { LanguageCode } from '../types';

type DateFilterOption = 'all' | 'today' | 'yesterday' | 'this_week' | 'custom';

interface AllStoriesProps {
  onEditStory?: (storyId: string) => void;
}

const AllStories: React.FC<AllStoriesProps> = ({ onEditStory }) => {
  const [storyGroups, setStoryGroups] = useState<StoryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<StoryGroup | null>(null);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  
  // Filter state
  const [dateFieldType, setDateFieldType] = useState<'created' | 'updated'>('created');
  const [dateFilter, setDateFilter] = useState<DateFilterOption>('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [users, setUsers] = useState<UserWithStories[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  // Author cache
  const [authorCache, setAuthorCache] = useState<Map<string, string>>(new Map());

  // Calculate date range based on filter
  const getDateRange = (): { dateFrom?: string; dateTo?: string } => {
    const now = new Date();
    let dateFrom: string | undefined;
    let dateTo: string | undefined;

    switch (dateFilter) {
      case 'today':
        dateFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
        dateTo = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).toISOString();
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        dateFrom = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate()).toISOString();
        dateTo = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999).toISOString();
        break;
      case 'this_week':
        const startOfWeek = new Date(now);
        const day = startOfWeek.getDay();
        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
        startOfWeek.setDate(diff);
        startOfWeek.setHours(0, 0, 0, 0);
        dateFrom = startOfWeek.toISOString();
        dateTo = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).toISOString();
        break;
      case 'custom':
        if (customDateFrom) {
          dateFrom = new Date(customDateFrom).toISOString();
        }
        if (customDateTo) {
          const endDate = new Date(customDateTo);
          endDate.setHours(23, 59, 59, 999);
          dateTo = endDate.toISOString();
        }
        break;
      default:
        // 'all' - no date filter
        break;
    }

    return { dateFrom, dateTo };
  };

  // Load users with stories
  useEffect(() => {
    const loadUsers = async () => {
      setLoadingUsers(true);
      const usersList = await getUsersWithStories();
      setUsers(usersList);
      setLoadingUsers(false);
    };
    loadUsers();
  }, []);

  // Load stories when filters change
  useEffect(() => {
    loadStories();
  }, [dateFieldType, dateFilter, customDateFrom, customDateTo, selectedUserId]);

  const loadStories = async () => {
    setLoading(true);
    try {
      const { dateFrom, dateTo } = getDateRange();
      
      const groups = await getStoryGroups({
        limit: 100,
        allUsers: true,
        authorId: selectedUserId !== 'all' ? selectedUserId : undefined,
        dateFrom,
        dateTo,
        dateField: dateFieldType,
      });

      setStoryGroups(groups);

      // Load author profiles for all unique authors
      const authorIds = new Set<string>();
      groups.forEach(group => {
        group.stories.forEach(story => {
          if (story.authorId) {
            authorIds.add(story.authorId);
          }
        });
      });

      // Fetch author profiles and cache them
      const newCache = new Map(authorCache);
      for (const authorId of authorIds) {
        if (!newCache.has(authorId)) {
          const profile = await getUserProfile(authorId);
          if (profile) {
            newCache.set(authorId, profile.full_name || profile.email || 'Unknown');
          }
        }
      }
      setAuthorCache(newCache);
    } catch (error) {
      console.error('Error loading stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLanguage = async (storyId: string, language: LanguageCode) => {
    // Navigate to WritingTool with this story
    if (onEditStory) {
      onEditStory(storyId);
    }
  };

  const handleGroupClick = (group: StoryGroup) => {
    if (group.availableLanguages.length === 1) {
      const story = group.stories.find(s => s.language === group.availableLanguages[0]);
      if (story) {
        handleSelectLanguage(story.id, story.language);
      }
    } else {
      setSelectedGroup(group);
      setShowLanguageSelector(true);
    }
  };

  const handleCreateTranslationFromGroup = async (groupId: string, targetLang: LanguageCode) => {
    // This would create a translation - for now just close the modal
    setShowLanguageSelector(false);
    setSelectedGroup(null);
  };

  const getAuthorName = (authorId: string | undefined): string | undefined => {
    if (!authorId) return undefined;
    return authorCache.get(authorId);
  };

  const clearFilters = () => {
    setDateFieldType('created');
    setDateFilter('all');
    setCustomDateFrom('');
    setCustomDateTo('');
    setSelectedUserId('all');
  };

  const hasActiveFilters = dateFilter !== 'all' || selectedUserId !== 'all';

  return (
    <div className="h-full bg-zinc-50 dark:bg-zinc-950 animate-fade-in">
      {/* Header */}
      <div className="bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-xl font-bold text-zinc-900 dark:text-white mb-4">
            All Stories
          </h1>
          
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Date Field Type Selector */}
            <div className="flex items-center gap-2">
              <select
                value={dateFieldType}
                onChange={(e) => setDateFieldType(e.target.value as 'created' | 'updated')}
                className="px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
              >
                <option value="created">Created Date</option>
                <option value="updated">Edited Date</option>
              </select>
            </div>

            {/* Date Filter */}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-zinc-500" />
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as DateFilterOption)}
                className="px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Custom Date Range */}
            {dateFilter === 'custom' && (
              <>
                <Input
                  type="date"
                  value={customDateFrom}
                  onChange={(e) => setCustomDateFrom(e.target.value)}
                  className="px-3 py-2 text-sm"
                />
                <span className="text-zinc-500 dark:text-zinc-400">to</span>
                <Input
                  type="date"
                  value={customDateTo}
                  onChange={(e) => setCustomDateTo(e.target.value)}
                  className="px-3 py-2 text-sm"
                />
              </>
            )}

            {/* User Filter */}
            <div className="flex items-center gap-2">
              <UserIcon className="h-4 w-4 text-zinc-500" />
              {loadingUsers ? (
                <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
              ) : (
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white min-w-[200px]"
                >
                  <option value="all">All Users</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.full_name || user.email || 'Unknown'}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                onClick={clearFilters}
                variant="outline"
                size="sm"
                className="flex items-center"
              >
                <X className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Story List */}
      <div className="max-w-7xl mx-auto p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : storyGroups.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-zinc-300 dark:text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-500 dark:text-zinc-400 mb-4">
              No stories found matching your filters.
            </p>
            {hasActiveFilters && (
              <Button
                onClick={clearFilters}
                variant="outline"
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {storyGroups.map((group, index) => {
              const authorName = getAuthorName(group.primaryStory.authorId);
              return (
                <div
                  key={group.groupId}
                  className="animate-fade-in animate-slide-in-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <StoryGroupCard
                    group={group}
                    onSelectLanguage={handleSelectLanguage}
                    onCreateTranslation={handleCreateTranslationFromGroup}
                    onGroupClick={handleGroupClick}
                    authorName={authorName}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Language Selector Modal */}
      {showLanguageSelector && selectedGroup && (
        <LanguageSelector
          group={selectedGroup}
          onSelectLanguage={handleSelectLanguage}
          onCreateTranslation={handleCreateTranslationFromGroup}
          onClose={() => {
            setShowLanguageSelector(false);
            setSelectedGroup(null);
          }}
        />
      )}
    </div>
  );
};

export default AllStories;
